import { CategoryVM } from "../data/types";

export interface CatNode {
  id: number;
  name: string;
  children: CatNode[];
}

const norm = (s: string) => s.trim().toLowerCase();

/**
 * Canonical grocery taxonomy. Leaves are the category names products actually
 * carry (from the backend or name-inference); parents are synthesized headings.
 * The sidebar is built from the categories PRESENT on the loaded products, so a
 * parent only appears when it has at least one product underneath it, and every
 * menu click maps to real products (Sprint 1 QA: category filtering).
 */
interface Tax { name: string; children?: Tax[]; }
const TAXONOMY: Tax[] = [
  { name: "Fresh Produce", children: [
    { name: "Fruits", children: [
      { name: "Apples" }, { name: "Bananas" }, { name: "Citrus" }, { name: "Berries" }, { name: "Tropical Fruits" },
    ] },
    { name: "Vegetables", children: [
      { name: "Leafy Greens" }, { name: "Root Vegetables" }, { name: "Cruciferous" }, { name: "Other Vegetables" },
    ] },
  ] },
  { name: "Dairy & Eggs", children: [
    { name: "Milk & Milk Alternatives" }, { name: "Cheese" }, { name: "Yogurt" }, { name: "Butter & Margarine" }, { name: "Eggs" },
  ] },
  { name: "Bakery", children: [
    { name: "Bread" }, { name: "Cakes & Pastries" }, { name: "Cookies & Biscuits" }, { name: "Bakery Snacks" },
  ] },
  { name: "Meat & Seafood" },
  { name: "Pantry Staples" },
  { name: "Beverages" },
  { name: "Snacks & Confectionery" },
  { name: "Frozen Foods" },
  { name: "Household & Personal Care" },
  { name: "Baby & Kids" },
];

let AUTO_ID = -1000;

/**
 * Build the sidebar tree from the set of category names present on products
 * (optionally plus backend category names). Accepts either a string[] of names
 * or CategoryVM[] (its names are used). Parents are kept only when a descendant
 * is present; unknown present categories are appended as top-level leaves.
 */
export function buildCategoryTree(input: string[] | CategoryVM[]): CatNode[] {
  const names: string[] = (input as any[]).map((x) =>
    typeof x === "string" ? x : (x && x.name) || ""
  ).filter(Boolean);
  const present = new Set(names.map(norm));
  if (!present.size) return [];

  const matched = new Set<string>();

  const build = (t: Tax): CatNode | null => {
    const kids = (t.children || []).map(build).filter(Boolean) as CatNode[];
    const selfPresent = present.has(norm(t.name));
    if (!selfPresent && kids.length === 0) return null;      // prune empty branch
    if (selfPresent) matched.add(norm(t.name));
    return { id: AUTO_ID--, name: t.name, children: kids };
  };

  const roots: CatNode[] = [];
  TAXONOMY.forEach((t) => { const n = build(t); if (n) roots.push(n); });

  // Mark every taxonomy leaf we rendered as matched so leftovers are truly extra.
  const markMatched = (n: CatNode) => { matched.add(norm(n.name)); n.children.forEach(markMatched); };
  roots.forEach(markMatched);

  // Any present category not covered by the taxonomy -> its own top-level row.
  const extras = Array.from(present).filter((p) => !matched.has(p));
  const seen = new Set<string>();
  names.forEach((orig) => {
    const k = norm(orig);
    if (extras.includes(k) && !seen.has(k)) {
      seen.add(k);
      roots.push({ id: AUTO_ID--, name: orig, children: [] });
    }
  });

  return roots;
}

/** A node's own name plus every descendant name (used to filter under a parent). */
export function descendantNames(node: CatNode): string[] {
  const out = [node.name];
  node.children.forEach((c) => out.push(...descendantNames(c)));
  return out;
}
